import { el } from '../../dom';
import { playSfx } from '../../audio/audio';

/**
 * Fisherman Fin's dialogue, and the only place a rod is sold.
 *
 * His original lines said this game had no fishing — because it did not. It has
 * one now, so the lines change with it: an NPC who still insists nothing has ever
 * been caught here, standing next to a working rod shop, would read as a bug.
 *
 * He is the ONLY seller. The forager keeper buys a rod off you at a loss but does
 * not stock one, which keeps the purchase a thing you do by walking to the water
 * and meeting the man who fishes it.
 */
export const FIN_LINES: readonly string[] = [
  'Fin, they call me. Fisherman Fin, if you want the whole of it.',
  'Been casting this bank since before your farm had a fence.',
  'There is more in this water than mud and greylings. A red one came up once. Once.',
  'Take a rod if you like. Stand near the water, cast, and see what the lake thinks of you.',
];

/** Shown instead of the last line when the player already owns a rod. */
export const FIN_HAS_ROD_LINE =
  'You have a rod already. Go on then — the fish are not going to argue with themselves.';

/**
 * What to do with the thing you just paid 150 coins for.
 *
 * OWNER (2026-08-17): "how to operate with him? I bought something from him —
 * where did it appear? I don't see it in the bag." The rod DOES land in the bag
 * (POST /fishing/rod → UserItem → GET /field/bag → the board, with a baked icon),
 * but nothing on screen ever said so, and nothing said a cast is a TAP ON THE
 * WATER — there is no rod tool in the hotbar to discover, so a player who was not
 * told has no way to find the mechanic. Both facts are stated here, on the panel
 * that sells the rod, and repeated whenever he is talked to while holding one.
 */
export const FIN_HOWTO: readonly string[] = [
  'The rod goes straight into your bag — open the bag and you will see it.',
  'To fish: stand on the bank and TAP THE WATER within two tiles of you.',
  'Every cast costs a little energy. Some come back empty. That is fishing.',
];

export interface FinShop {
  /** True when the player already owns a rod — Fin stops offering one. */
  hasRod: boolean;
  price: number;
  /** Resolves once the purchase has actually landed, so the panel can re-render. */
  buy: () => Promise<void>;
}

export function openFishermanDialog(
  host: HTMLElement,
  onClose?: () => void,
  shop?: FinShop,
): () => void {
  const layer = el('div', 'absolute inset-0 z-40 flex items-center justify-center bg-black/55 backdrop-blur-[1px]');
  const panel = el('div',
    'relative w-[min(92vw,420px)] rounded-2xl border-2 border-sky-300/50 bg-[#182028]/95 shadow-lift p-5 text-sky-50 font-body');
  layer.appendChild(panel);
  host.appendChild(layer);

  let closed = false;
  const close = () => { if (closed) return; closed = true; layer.remove(); onClose?.(); };
  layer.addEventListener('pointerdown', (e) => { if (e.target === layer) close(); });

  let i = 0;
  const render = () => {
    panel.replaceChildren();
    panel.appendChild(el('button',
      'tb-close-x absolute top-2 right-3 text-sky-200/70 hover:text-sky-100 text-xl leading-none',
      { onClick: close }, '×'));
    panel.appendChild(el('div', 'text-center text-2xl font-heading tracking-wide text-sky-200 mb-1', {}, 'Fisherman Fin'));
    panel.appendChild(el('div', 'text-center text-xs uppercase tracking-[0.2em] text-sky-300/60 mb-3', {}, 'On the bank'));
    panel.appendChild(el('p', 'text-sm italic text-sky-100/90 text-center leading-relaxed mb-4 px-2', {}, `“${FIN_LINES[i]}”`));

    const last = i >= FIN_LINES.length - 1;
    // On his last line he makes the offer — or, if the player already has a rod,
    // says so rather than selling a second one nobody can use.
    if (last && shop) {
      if (shop.hasRod) {
        panel.appendChild(el('p', 'text-xs text-sky-200/70 text-center mb-3 px-2', {}, FIN_HAS_ROD_LINE));
        // The how-to rides with "you already have one" — including the render
        // right after a purchase, which is the moment the player is asking
        // "where did it go?".
        const how = el('div', 'tb-fin-howto rounded-lg bg-sky-950/60 border border-sky-400/25 p-3 mb-3 text-left');
        how.appendChild(el('div', 'text-[11px] uppercase tracking-[0.18em] text-sky-300/70 mb-1.5', {}, 'Using the rod'));
        for (const line of FIN_HOWTO) {
          how.appendChild(el('p', 'text-xs text-sky-100/85 leading-relaxed mb-1', {}, `• ${line}`));
        }
        panel.appendChild(how);
      } else {
        const buy = el('button',
          'w-full min-h-[44px] py-2.5 mb-2 rounded-lg font-heading tracking-wide bg-amber-600 hover:bg-amber-500 text-amber-50 cursor-pointer transition',
          {}, `Buy a rod — ${shop.price} coins`) as HTMLButtonElement;
        buy.addEventListener('click', () => {
          buy.disabled = true;                    // a second click buys nothing but confusion
          buy.textContent = 'Handing it over…';
          void shop.buy().then(() => { shop.hasRod = true; render(); }).catch(() => {
            buy.disabled = false;
            buy.textContent = `Buy a rod — ${shop.price} coins`;
          });
        });
        panel.appendChild(buy);
      }
    }
    const btn = el('button',
      'w-full min-h-[44px] py-2.5 rounded-lg font-heading tracking-wide bg-sky-600 hover:bg-sky-500 text-sky-50 cursor-pointer transition',
      {}, last ? 'Leave him to it' : 'Go on…');
    (btn as HTMLButtonElement).addEventListener('click', () => {
      playSfx('ui_click');
      // Advancing past the last line closes: a dialogue that loops for ever gives
      // the player no signal that they have heard everything he has to say.
      if (last) { close(); return; }
      i++;
      render();
    });
    panel.appendChild(btn);
  };
  render();
  return close;
}
